import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CountryDistributionItem,
  DashboardStats,
  FundraisingResponse,
  LatestDelivery,
  ListResponse,
  MonthlyAddedItem,
  NotificationResponse,
  ProfileCategoryCountsResponse,
  ProfileResponse,
  SourceSplitItem,
  TopCompatibleLP,
} from '@shared/schemas';

import type { DashboardScope } from '../domain/scope';
import { initialState, LOADED_PANELS } from './dashboard.state';

/**
 * Dashboard slice. Reducers are pure state transitions only; the fetches
 * live in the saga, which dispatches these per-panel transitions as each endpoint
 * settles. `loadStarted` marks every shown panel loading at once because the single
 * `dashboardRequested` signal always (re)loads the whole board; each panel then
 * succeeds or fails independently.
 */
const slice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    loadStarted: (state) => {
      for (const key of LOADED_PANELS) {
        state[key].loading = true;
        state[key].error = null;
      }
    },

    notificationsLoaded: (state, action: PayloadAction<NotificationResponse[]>) => {
      state.notifications = { data: action.payload, loading: false, error: null };
    },
    notificationsFailed: (state, action: PayloadAction<string>) => {
      state.notifications.loading = false;
      state.notifications.error = action.payload;
    },

    listsLoaded: (state, action: PayloadAction<ListResponse[]>) => {
      state.lists = { data: action.payload, loading: false, error: null };
    },
    listsFailed: (state, action: PayloadAction<string>) => {
      state.lists.loading = false;
      state.lists.error = action.payload;
    },

    countriesLoaded: (state, action: PayloadAction<CountryDistributionItem[]>) => {
      state.countries = { data: action.payload, loading: false, error: null };
    },
    countriesFailed: (state, action: PayloadAction<string>) => {
      state.countries.loading = false;
      state.countries.error = action.payload;
    },

    topLpsLoaded: (state, action: PayloadAction<TopCompatibleLP[]>) => {
      state.topLps = { data: action.payload, loading: false, error: null };
    },
    topLpsFailed: (state, action: PayloadAction<string>) => {
      state.topLps.loading = false;
      state.topLps.error = action.payload;
    },

    trackedLpsLoaded: (state, action: PayloadAction<ProfileResponse[]>) => {
      state.trackedLps = { data: action.payload, loading: false, error: null };
    },
    trackedLpsFailed: (state, action: PayloadAction<string>) => {
      state.trackedLps.loading = false;
      state.trackedLps.error = action.payload;
    },

    statsLoaded: (state, action: PayloadAction<DashboardStats | null>) => {
      state.stats = { data: action.payload, loading: false, error: null };
    },
    statsFailed: (state, action: PayloadAction<string>) => {
      state.stats.loading = false;
      state.stats.error = action.payload;
    },

    latestFundraisingLoaded: (state, action: PayloadAction<FundraisingResponse | null>) => {
      state.latestFundraising = { data: action.payload, loading: false, error: null };
    },
    latestFundraisingFailed: (state, action: PayloadAction<string>) => {
      state.latestFundraising.loading = false;
      state.latestFundraising.error = action.payload;
    },

    sourceSplitLoaded: (state, action: PayloadAction<SourceSplitItem[]>) => {
      state.sourceSplit = { data: action.payload, loading: false, error: null };
    },
    sourceSplitFailed: (state, action: PayloadAction<string>) => {
      state.sourceSplit.loading = false;
      state.sourceSplit.error = action.payload;
    },

    categoryCountsLoaded: (state, action: PayloadAction<ProfileCategoryCountsResponse | null>) => {
      state.categoryCounts = { data: action.payload, loading: false, error: null };
    },
    categoryCountsFailed: (state, action: PayloadAction<string>) => {
      state.categoryCounts.loading = false;
      state.categoryCounts.error = action.payload;
    },

    monthlyAddedLoaded: (state, action: PayloadAction<MonthlyAddedItem[]>) => {
      state.monthlyAdded = { data: action.payload, loading: false, error: null };
    },
    monthlyAddedFailed: (state, action: PayloadAction<string>) => {
      state.monthlyAdded.loading = false;
      state.monthlyAdded.error = action.payload;
    },

    latestDeliveryLoaded: (state, action: PayloadAction<LatestDelivery | null>) => {
      state.latestDelivery = { data: action.payload, loading: false, error: null };
    },
    latestDeliveryFailed: (state, action: PayloadAction<string>) => {
      state.latestDelivery.loading = false;
      state.latestDelivery.error = action.payload;
    },

    scopeSet: (state, action: PayloadAction<DashboardScope>) => {
      state.scope = action.payload;
      state.refreshing = true;
    },
    refreshDone: (state) => {
      state.refreshing = false;
    },
  },
});

export const dashboardActions = slice.actions;
export const dashboardReducer = slice.reducer;
